import { AlertTriangle, CalendarDays, FileText, Info, MapPin, Paperclip } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  POST_TYPE_LABELS,
  formatDate,
  formatDateLong,
  formatDateTime,
  formatWallClock,
  coverImageStyle,
  weekdayLabel,
  type NoticeSeverity,
} from '@paroquia/shared'
import { cn } from '@/lib/utils'
import type {
  EventCard as EventCardData,
  PostCard as PostCardData,
  PostAttachment,
  ResolvedDay,
  UrgentNotice,
} from '@/lib/portal-types'

/*
 * Componentes do portal publico.
 *
 * Reunidos num arquivo por serem pequenos e usados juntos: cartao de noticia,
 * faixa de aviso, bloco de horarios e cartao de evento aparecem em combinacoes
 * diferentes na home, na listagem e na busca.
 */

// --- Avisos urgentes (RF01.3) ---------------------------------------------

const ESTILO_AVISO: Record<NoticeSeverity, { caixa: string; icone: typeof Info }> = {
  INFO: { caixa: 'border-info bg-info-suave text-info', icone: Info },
  ATENCAO: { caixa: 'border-aviso bg-aviso-suave text-aviso', icone: AlertTriangle },
  // Vermelho reservado ao urgente: se toda faixa fosse vermelha, nenhuma seria.
  URGENTE: { caixa: 'border-perigo bg-perigo-suave text-perigo', icone: AlertTriangle },
}

export function FaixaDeAvisos({ avisos }: { avisos: UrgentNotice[] }) {
  if (avisos.length === 0) return null

  return (
    <section aria-label="Avisos importantes" className="space-y-2">
      {avisos.map((aviso) => {
        const estilo = ESTILO_AVISO[aviso.severity]
        const Icone = estilo.icone
        return (
          <div
            key={aviso.id}
            // role=alert apenas no urgente: o leitor de tela interromper a
            // leitura por um informativo seria ruido.
            role={aviso.severity === 'URGENTE' ? 'alert' : undefined}
            className={cn('flex items-start gap-3 rounded-lg border-2 p-4', estilo.caixa)}
          >
            <Icone className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              {aviso.severity === 'URGENTE' && (
                <p className="text-xs font-bold tracking-wide uppercase">Aviso urgente</p>
              )}
              <p className="font-medium">{aviso.message}</p>
              {aviso.linkUrl && (
                <a
                  href={aviso.linkUrl}
                  className="mt-1 inline-block font-semibold underline underline-offset-2"
                >
                  {aviso.linkLabel || 'Saiba mais'}
                </a>
              )}
            </div>
          </div>
        )
      })}
    </section>
  )
}

// --- Cartao de noticia ----------------------------------------------------

export function CartaoDeNoticia({
  post,
  destaque = false,
}: {
  post: PostCardData
  destaque?: boolean
}) {
  return (
    <article
      className={cn(
        'group overflow-hidden rounded-lg border border-borda bg-cartao transition-shadow hover:shadow-md',
        destaque && 'sm:col-span-2',
      )}
    >
      <Link to={`/noticias/${post.slug}`} className="block">
        {post.cover && (
          <img
            src={destaque ? post.cover.medium : post.cover.thumb}
            // O alt vem do que a secretaria escreveu; vazio e melhor que
            // repetir o titulo, que o leitor de tela ja vai ler no link.
            alt={post.cover.alt ?? ''}
            loading="lazy"
            className={cn('w-full object-cover', destaque ? 'aspect-[16/9]' : 'aspect-[4/3]')}
          />
        )}
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {post.type !== 'NOTICIA' && (
              <span className="rounded bg-primario-suave px-2 py-0.5 font-semibold text-primario">
                {POST_TYPE_LABELS[post.type]}
              </span>
            )}
            {post.category && (
              <span
                className="rounded px-2 py-0.5 font-medium"
                style={
                  post.category.color
                    ? { backgroundColor: `${post.category.color}1a`, color: post.category.color }
                    : undefined
                }
              >
                {post.category.name}
              </span>
            )}
            {post.publishedAt && (
              <time dateTime={post.publishedAt} className="text-suave">
                {formatDate(post.publishedAt)}
              </time>
            )}
          </div>

          <h3
            className={cn(
              'mt-2 font-serif font-semibold group-hover:underline',
              destaque ? 'text-2xl' : 'text-lg',
            )}
          >
            {post.title}
          </h3>

          {post.excerpt && (
            <p className="mt-1.5 text-sm text-suave">{post.excerpt}</p>
          )}
        </div>
      </Link>
    </article>
  )
}

// --- Anexos (RF01.1) ------------------------------------------------------

export function ListaDeAnexos({ anexos }: { anexos: PostAttachment[] }) {
  if (anexos.length === 0) return null

  const tamanho = (bytes: number) =>
    bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`

  return (
    <section className="mt-8 rounded-lg border border-borda bg-afundado p-4">
      <h2 className="flex items-center gap-2 font-serif text-lg font-semibold">
        <Paperclip className="size-4" aria-hidden="true" />
        Documentos anexos
      </h2>
      <ul className="mt-3 space-y-2">
        {anexos.map((anexo) => (
          <li key={anexo.id}>
            <a
              href={anexo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-medium underline underline-offset-2"
            >
              <FileText className="size-4 shrink-0" aria-hidden="true" />
              <span>{anexo.label}</span>
              <span className="text-sm font-normal text-suave">
                (PDF, {tamanho(anexo.byteSize)})
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  )
}

// --- Horarios -------------------------------------------------------------

export function BlocoDeHorarios({ dias }: { dias: ResolvedDay[] }) {
  const comCelebracao = dias.filter((dia) => dia.slots.length > 0)
  if (comCelebracao.length === 0) {
    return (
      <p className="text-sm text-suave">
        Nenhuma celebracao cadastrada para os proximos dias.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      {comCelebracao.map((dia) => (
        <div key={dia.date}>
          <h3 className="font-serif font-semibold">
            {weekdayLabel(dia.weekday)}
            <span className="ml-2 text-sm font-normal text-suave">
              {formatDate(`${dia.date}T12:00:00Z`)}
            </span>
          </h3>

          {dia.fechado && dia.fechadoMotivo && (
            <p
              role="alert"
              className="mt-1 rounded border-2 border-perigo bg-perigo-suave px-3 py-2 text-sm font-medium text-perigo"
            >
              Nao havera celebracao: {dia.fechadoMotivo}
            </p>
          )}

          <ul className="mt-1.5 divide-y divide-borda">
            {dia.slots.map((slot) => (
              <li
                key={`${slot.scheduleId}-${slot.date}`}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2"
              >
                <span
                  className={cn(
                    'min-w-16 font-semibold',
                    slot.cancelled && 'text-perigo line-through',
                  )}
                >
                  {formatWallClock(slot.time)}
                </span>
                <span className={cn(slot.cancelled && 'text-suave line-through')}>
                  {slot.kind === 'MISSA' ? 'Missa' : slot.kind === 'CONFISSAO' ? 'Confissoes' : slot.kind === 'ADORACAO' ? 'Adoracao' : slot.kind === 'TERCO' ? 'Terco' : slot.kind === 'NOVENA' ? 'Novena' : 'Celebracao'}
                </span>
                <span className="flex items-center gap-1 text-sm text-suave">
                  <MapPin className="size-3.5" aria-hidden="true" />
                  {slot.location}
                </span>
                {slot.note && (
                  <span className="text-sm text-suave">({slot.note})</span>
                )}

                {slot.cancelled && !dia.fechado && (
                  <span className="rounded bg-perigo-suave px-2 py-0.5 text-xs font-bold text-perigo">
                    CANCELADA
                  </span>
                )}
                {/* O fiel precisa ver o que MUDOU, nao so o horario novo. */}
                {slot.changed && !slot.cancelled && (
                  <span className="rounded bg-aviso-suave px-2 py-0.5 text-xs font-bold text-aviso">
                    {slot.originalTime
                      ? `ALTERADO (era ${formatWallClock(slot.originalTime)})`
                      : 'ALTERADO'}
                  </span>
                )}
                {slot.changeReason && !dia.fechado && (
                  <span className="w-full text-sm text-suave">
                    {slot.changeReason}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export function GradeSemanal({ grade }: { grade: { dayOfWeek: number; kindLabel: string; timeLabel: string; location: string; note: string | null }[] }) {
  const porDia = new Map<number, typeof grade>()
  for (const linha of grade) {
    porDia.set(linha.dayOfWeek, [...(porDia.get(linha.dayOfWeek) ?? []), linha])
  }

  if (grade.length === 0) {
    return (
      <p className="text-suave">
        A grade de horarios ainda nao foi cadastrada pela secretaria.
      </p>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[...porDia.entries()]
        .sort(([a], [b]) => a - b)
        .map(([dia, linhas]) => (
          <div
            key={dia}
            className="rounded-lg border border-borda bg-cartao p-4"
          >
            <h3 className="font-serif text-lg font-semibold">{weekdayLabel(dia)}</h3>
            <ul className="mt-2 space-y-2">
              {linhas.map((linha, indice) => (
                <li key={indice} className="text-sm">
                  <span className="font-semibold">{linha.timeLabel}</span>
                  <span className="mx-1.5">-</span>
                  <span>{linha.kindLabel}</span>
                  <span className="block text-suave">
                    {linha.location}
                    {linha.note ? ` (${linha.note})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  )
}

// --- Eventos --------------------------------------------------------------

export function CartaoDeEvento({ evento }: { evento: EventCardData }) {
  const situacao = evento.inscricoes

  return (
    <article className="group overflow-hidden rounded-lg border border-borda bg-cartao transition-shadow hover:shadow-md">
      <Link to={`/eventos/${evento.slug}`} className="block">
        {evento.cover && (
          /* Fundo definido: no modo "Encolher para caber" as faixas ao redor
             da imagem ficam visiveis e precisam de cor. */
          <div className="aspect-[16/9] w-full bg-primario-suave">
            <img
              src={evento.cover.medium}
              alt={evento.cover.alt ?? ''}
              loading="lazy"
              className="h-full w-full"
              style={coverImageStyle(evento.coverFit, evento.coverPosition)}
            />
          </div>
        )}
        <div className="p-4">
          <p className="flex items-center gap-1.5 text-sm text-suave">
            <CalendarDays className="size-4" aria-hidden="true" />
            <time dateTime={evento.startsAt}>{formatDateTime(evento.startsAt)}</time>
          </p>

          <h3 className="mt-1.5 font-serif text-xl font-semibold group-hover:underline">
            {evento.title}
          </h3>

          <p className="mt-1 flex items-center gap-1.5 text-sm text-suave">
            <MapPin className="size-4" aria-hidden="true" />
            {evento.location}
          </p>

          {evento.summary && <p className="mt-2 text-sm">{evento.summary}</p>}

          {situacao && (
            <p className="mt-3 text-sm font-medium">
              {situacao.aberta && !situacao.lotado && (
                <span className="text-sucesso">
                  Inscricoes abertas
                  {evento.vagasRestantes !== null && evento.vagasRestantes !== undefined
                    ? ` - ${evento.vagasRestantes} ${evento.vagasRestantes === 1 ? 'vaga' : 'vagas'}`
                    : ''}
                </span>
              )}
              {situacao.aberta && situacao.lotado && (
                <span className="text-aviso">Lotado - entra na lista de espera</span>
              )}
              {!situacao.aberta && (
                <span className="text-suave">{situacao.motivo}</span>
              )}
            </p>
          )}
        </div>
      </Link>
    </article>
  )
}

// --- Estado vazio ---------------------------------------------------------

export function Vazio({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="rounded-lg border border-dashed border-borda p-10 text-center">
      <p className="font-serif text-lg font-semibold">{titulo}</p>
      <p className="mt-1 text-sm text-suave">{texto}</p>
    </div>
  )
}

export function DataLonga({ valor }: { valor: string }) {
  return <time dateTime={valor}>{formatDateLong(valor)}</time>
}
